// Client-side pattern dictionary + naive extractor.
// Returns candidates with confidence + suggested slot values.

const PATTERNS = [
	{
		id: "retail-commerce",
		name: "Retail commerce",
		keywords: ["retail", "shop", "store", "ecommerce", "e-commerce", "commerce", "sell", "sku", "cart", "checkout", "d2c", "dtc"],
		slots: [
			{ label: "Customer type", value: "Consumer buyers" },
			{ label: "Core offer", value: "Physical or digital goods sold direct" },
			{ label: "Pricing model", value: "Per-unit price, discounts, bundles" },
			{ label: "Primary channel", value: "Storefront + marketplace" },
		],
	},
	{
		id: "subscription-saas",
		name: "Subscription SaaS",
		keywords: ["saas", "subscription", "software", "platform", "seats", "tiers", "monthly", "annual", "arr", "mrr"],
		slots: [
			{ label: "Customer type", value: "Business teams" },
			{ label: "Core offer", value: "Software access on recurring terms" },
			{ label: "Pricing model", value: "Per-seat, tiered plans" },
			{ label: "Primary channel", value: "Self-serve + sales-assisted" },
		],
	},
	{
		id: "marketplace",
		name: "Two-sided marketplace",
		keywords: ["marketplace", "two-sided", "supply", "demand", "connect", "buyers", "sellers", "listings"],
		slots: [
			{ label: "Customer type", value: "Buyers and sellers" },
			{ label: "Core offer", value: "Matched transactions" },
			{ label: "Pricing model", value: "Take-rate on transactions" },
			{ label: "Primary channel", value: "Web marketplace" },
		],
	},
	{
		id: "b2b-services",
		name: "B2B services",
		keywords: ["consulting", "agency", "services", "advisory", "consultancy", "engagement", "retainer", "professional services"],
		slots: [
			{ label: "Customer type", value: "Enterprise clients" },
			{ label: "Core offer", value: "Expert engagements and deliverables" },
			{ label: "Pricing model", value: "Retainer or fixed-fee engagements" },
			{ label: "Primary channel", value: "Direct sales + referrals" },
		],
	},
	{
		id: "financial-services",
		name: "Financial services",
		keywords: ["bank", "banking", "finance", "lending", "loans", "credit", "insurance", "fintech", "payments", "wealth"],
		slots: [
			{ label: "Customer type", value: "Retail or business account holders" },
			{ label: "Core offer", value: "Regulated financial products" },
			{ label: "Pricing model", value: "Fees, spreads, premiums" },
			{ label: "Primary channel", value: "App + branch" },
		],
	},
	{
		id: "logistics",
		name: "Logistics & supply chain",
		keywords: ["logistics", "supply chain", "warehouse", "fulfilment", "fulfillment", "shipping", "delivery", "fleet"],
		slots: [
			{ label: "Customer type", value: "Shippers and carriers" },
			{ label: "Core offer", value: "Movement of goods across the chain" },
			{ label: "Pricing model", value: "Per-shipment or contracted rates" },
			{ label: "Primary channel", value: "Operations network" },
		],
	},
	{
		id: "media",
		name: "Media & content",
		keywords: ["media", "content", "publisher", "publishing", "editorial", "creator", "newsroom", "podcast", "video"],
		slots: [
			{ label: "Customer type", value: "Readers, viewers, listeners" },
			{ label: "Core offer", value: "Editorial and creative content" },
			{ label: "Pricing model", value: "Subscription + advertising" },
			{ label: "Primary channel", value: "Web + apps" },
		],
	},
	{
		id: "manufacturing",
		name: "Manufacturing",
		keywords: ["factory", "manufacturing", "industrial", "production", "plant", "assembly", "wholesale"],
		slots: [
			{ label: "Customer type", value: "Wholesale and industrial buyers" },
			{ label: "Core offer", value: "Produced goods at scale" },
			{ label: "Pricing model", value: "Wholesale price per unit" },
			{ label: "Primary channel", value: "Distributor network" },
		],
	},
];

export const FALLBACK_CATEGORIES = PATTERNS.map((p) => p.name).concat([
	"Healthcare",
	"Education",
	"Real estate",
	"Travel & hospitality",
	"Energy & utilities",
	"Something else",
]);

export function extractPatterns({ description = "", url = "", tools = [], analogy = "" }) {
	const haystack = [description, url, tools.join(" "), analogy].join(" ").toLowerCase();
	const words = haystack.split(/[^a-z0-9]+/).filter(Boolean).length;

	const scored = PATTERNS.map((p) => {
		let hits = 0;
		for (const kw of p.keywords) {
			if (haystack.includes(kw)) hits += 1;
		}
		return { pattern: p, hits };
	})
		.filter((s) => s.hits > 0)
		.sort((a, b) => b.hits - a.hits);

	// Confidence heuristic
	const results = scored.slice(0, 3).map((s, idx) => {
		let confidence = "medium";
		if (s.hits >= 3 || (idx === 0 && s.hits >= 2 && words >= 12)) confidence = "high";
		if (s.hits === 1 && words < 8) confidence = "low";
		return { ...s.pattern, hits: s.hits, confidence };
	});

	const thin = words < 6 || results.length === 0 || results.every((r) => r.confidence === "low");
	return { results, thin, wordCount: words };
}
